// <purpose>VNB Klärfall-Checkliste editor — the centerpiece of the scaffold reply flow.
// An incoming B2B Marktpartner email is turned into a deterministic fill-in reply
// "scaffold" (segments). This editor renders the draft WHOLE and read-only on top,
// with each open gap (placeholder / unconfirmed-hybrid) bound to a docked checklist
// row. Presentational only: all state is owned by the caller via the `draft` prop
// (useKlaerfallDraft hook). Footer actions live in TicketDetail; why a row has no letter is the
// header's and the footer's to say — this editor only hosts the zone chip.</purpose>
// <dependencies>useCategoryMeta (ClassificationSchemaContext) for the marktprozess-derived TINT
// the gap chips are drawn in — no longer for a header label, see the header below;
// KlaerfallDraft from useKlaerfallDraft; theme tokens.</dependencies>
// <constraints>Layout-stability rule: gap chips / checklist rows pre-allocate space; the pencil
// correction toggles inline without reflowing the draft prose. Per-slot 3-state UI (check / cross /
// pencil SVG icons, Enter/Esc keyboard shortcuts). Whether fields are open is the footer's
// „Felder" pill to say — this editor draws no completeness line.</constraints>
import React, { useState, useEffect } from 'react';
import { ScaffoldSegment } from '../types';
import { theme } from '../theme';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { KlaerfallDraft } from '../hooks/useKlaerfallDraft';
import { CheckIcon, CrossIcon, PencilIcon, ZoneChip, ReviewChip } from './shared';
import { marktprozessTint } from '../services/categoryMeta';

interface KlaerfallEditorProps {
  segments: ScaffoldSegment[];
  marktprozess: string;
  // D3 hand-off zone (reply-engine envelope); absent on pre-engine tickets AND when the
  // tenant's HANDOFF_ZONES lever is off — ZoneChip itself renders an invisible placeholder
  // then, so the header's reserved slot never collapses.
  zone?: string | null;
  draft: KlaerfallDraft;
  // Demo-only "Simulierte Systemdaten": when enabled, a violet button fills the open system slots.
  // onSimulate performs the fill and resolves the filled segment indices (for the arrival flash).
  simulateEnabled?: boolean;
  onSimulate?: () => Promise<number[]>;
}

// Small database/lookup glyph — signals "value fetched from the customer system".
const DbIcon: React.FC<{ size?: number; color?: string }> = ({ size = 13, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <ellipse cx="12" cy="5" rx="7" ry="2.6" stroke={color} strokeWidth="1.8" />
    <path d="M5 5v14c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6V5" stroke={color} strokeWidth="1.8" />
    <path d="M5 12c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6" stroke={color} strokeWidth="1.8" />
  </svg>
);

// Navy numeric chip — replaces unicode circled glyphs (①..⑳) that looked dated.
const NumChip: React.FC<{ n: number }> = ({ n }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 16, height: 16, borderRadius: '50%',
    background: '#06105A', color: '#fff',
    fontSize: 10, fontWeight: 700, lineHeight: 1,
    flexShrink: 0, fontFamily: 'Poppins, sans-serif',
  }}>{n}</span>
);

const isSlot = (s: ScaffoldSegment): s is Extract<ScaffoldSegment, { kind: 'slot' }> => s.kind === 'slot';

// First-draft German — operator reviews. Flags a segment the GenerativeAnswerHandler realized as
// free LLM prose (no fixed template/system value backs it) so the rep knows to read it, not just
// confirm slots. 'grounded' (deterministic) segments and pre-groundingKind segments render bare.
const GENERATIVE_CHIP_LABEL = 'KI-Antwort — bitte prüfen';
const GENERATIVE_CHIP_HOVER = 'Frei formulierter KI-Text ohne festen Textbaustein — bitte inhaltlich prüfen';

const KlaerfallEditor: React.FC<KlaerfallEditorProps> = ({
  segments, marktprozess, zone, draft, simulateEnabled, onSimulate,
}) => {
  const cm = useCategoryMeta();
  const tint = marktprozessTint(cm.color(marktprozess));

  // Simulierte Systemdaten (demo): loading gesture + one-shot arrival flash on freshly-filled slots.
  const [simulating, setSimulating] = useState(false);
  const [justFilled, setJustFilled] = useState<Set<number>>(new Set());
  useEffect(() => {
    if (justFilled.size === 0) return;
    const t = window.setTimeout(() => setJustFilled(new Set()), 1300);
    return () => window.clearTimeout(t);
  }, [justFilled]);
  const handleSimClick = async () => {
    if (simulating || !onSimulate) return;
    setSimulating(true);
    try {
      const filled = await onSimulate();
      if (filled.length > 0) setJustFilled(new Set(filled));
    } finally {
      setSimulating(false);
    }
  };

  // All slot segment indices (confirmed included) — each gets a numbered checklist row.
  // Confirmed slots render locked; open slots render the editable 3-state input.
  const allSlotIndices = segments
    .map((s, i) => (isSlot(s) ? i : -1))
    .filter(i => i >= 0);

  // Checklist is shown when there are any slot segments; frame-only scaffolds (no slots) skip it.
  const hasSlots = allSlotIndices.length > 0;

  // ── styles ──
  const card: React.CSSProperties = {
    fontFamily: theme.font.family, borderRadius: theme.borderRadius.md,
    boxShadow: theme.shadows.cardLifted, backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.borderLight}`, overflow: 'hidden',
  };
  // gapChip is component-scoped so it can read the marktprozess-derived tint.
  const gapChip: React.CSSProperties = {
    backgroundColor: tint.fill, border: `1px dashed ${tint.border}`, color: tint.text,
    borderRadius: theme.borderRadius.sm, padding: '1px 6px', fontWeight: 600,
    whiteSpace: 'nowrap',
  };
  const mailGreen: React.CSSProperties = {
    color: '#0f5f57', textDecoration: 'underline', textDecorationStyle: 'dotted',
    textDecorationColor: '#2c8f80',
  };
  const iconBtn = (bg: string): React.CSSProperties => ({
    width: 22, height: 22, borderRadius: 5, border: 'none', background: bg,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  });

  // ── header: the zone chip alone ──
  // The ticket header is the one home of the category and the Stufe (2026-10-30: one fact, one
  // home), so this band carries only the hand-off zone — and exists only when a zone is served
  // (the zone lands with the suggestion, in the same paint as the letter; nothing to reserve).

  return (
    <div style={card}>
      {zone && (
        <div data-testid="klaerfall-zone-band" style={{
          display: 'flex', alignItems: 'center', gap: theme.spacing.sm, flexWrap: 'wrap',
          padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
          borderBottom: `1px solid ${theme.colors.bgLight}`, backgroundColor: theme.colors.bgPage,
        }}>
          <ZoneChip zone={zone} style={{ marginLeft: 'auto' }} />
        </div>
      )}

      {/* Draft (read-only) */}
      <div style={{
        padding: theme.spacing.md, fontSize: 13, lineHeight: 1.9, color: theme.colors.navyMid,
        whiteSpace: 'pre-wrap',
      }}>
        {segments.map((s, i) => {
          if (!isSlot(s)) {
            if (s.groundingKind === 'generative') {
              // The reply engine bakes the inter-paragraph "\n\n" separator INTO this segment's
              // value (every segment but the last carries it) — so the chip must anchor after the
              // answer prose but BEFORE that trailing separator, or it visually lands at the head
              // of the next paragraph once whiteSpace:pre-wrap renders the newlines. Split off the
              // trailing whitespace and render it after the chip instead.
              const m = s.value?.match(/^([\s\S]*?)(\s*)$/);
              return (
                <React.Fragment key={i}>
                  {m ? m[1] : s.value}
                  {/* Layout-stability: the chip is part of THIS segment's initial render (never added
                      after mount via a later effect), so its appearance never reflows neighbors. */}
                  <ReviewChip label={GENERATIVE_CHIP_LABEL} hover={GENERATIVE_CHIP_HOVER} style={{ marginLeft: 6, verticalAlign: 'middle' }} />
                  {m ? m[2] : null}
                </React.Fragment>
              );
            }
            return (
              <React.Fragment key={i}>
                {s.value}
              </React.Fragment>
            );
          }

          // confirmed slot → inline green value (covers both 'filled' init and user-confirmed)
          if (draft.isConfirmed(i)) {
            return (
              <span key={i} className={justFilled.has(i) ? 'stx-slot-filled' : undefined} style={mailGreen}>
                {draft.values[i] ?? s.value ?? ''}
              </span>
            );
          }

          // open slot → numbered gap chip; rowNo = position among ALL slot segments (not just open)
          const rowNo = allSlotIndices.indexOf(i) + 1;
          return (
            <span
              key={i}
              // Named by its SLOT, so a surface that hosts several of these letters at once (the
              // wim draft area renders one per draft kind) can count the gaps in one of them
              // without reaching for the label text, which is the tenant's own German.
              data-testid={`klaerfall-gap-${s.slotId}`}
              style={{ ...gapChip, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <NumChip n={rowNo} /> {s.label}
            </span>
          );
        })}
      </div>

      {/* The docked checklist (a frame-only letter has nothing to fill and shows none) */}
      {hasSlots ? (
        <div style={{ borderTop: `1px solid ${theme.colors.bgLight}` }}>
          {/* Simulierte Systemdaten (demo): fill the open system slots from the (simulated) customer
              system. Sits above the checklist — right where the gaps are — and disappears once filled. */}
          {simulateEnabled && (
            <div style={{
              padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
              borderBottom: `1px solid ${theme.colors.bgLight}`, backgroundColor: '#faf7ff',
              animation: 'stx-fadeIn 0.3s ease',
            }}>
              <button
                type="button"
                onClick={handleSimClick}
                disabled={simulating}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: `${theme.spacing.sm}px ${theme.spacing.md + 2}px`,
                  fontSize: 12.5, fontWeight: 600, fontFamily: theme.font.family,
                  color: theme.colors.white,
                  backgroundColor: simulating ? theme.colors.demoAccentHover : theme.colors.demoAccent,
                  border: 'none', borderRadius: theme.borderRadius.md,
                  cursor: simulating ? 'wait' : 'pointer',
                  transition: 'background-color 0.15s ease',
                }}
              >
                <span aria-hidden style={{ display: 'inline-flex', width: 13, height: 13, ...(simulating ? { animation: 'stx-spin 0.8s linear infinite' } : {}) }}>
                  {simulating
                    ? <span style={{ width: 13, height: 13, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.45)', borderTopColor: '#fff', boxSizing: 'border-box' }} />
                    : <DbIcon size={13} />}
                </span>
                {simulating ? 'Systemdaten werden abgerufen…' : 'Simulierte Systemdaten'}
              </button>
              <div style={{ fontSize: 11, color: theme.colors.navyMuted, marginTop: 5 }}>
                Befüllt die offenen Felder mit Werten aus dem Kundensystem (Demo).
              </div>
            </div>
          )}
          {allSlotIndices.map((i, idx) => {
            const seg = segments[i] as Extract<ScaffoldSegment, { kind: 'slot' }>;
            const rowNo = idx + 1;
            const isHybrid = seg.source === 'hybrid';
            const isSystem = seg.source === 'system';
            const confirmed = draft.isConfirmed(i);
            const value = draft.values[i] ?? '';
            const hasValue = value.trim().length > 0;
            const tintSolid = tint.border;
            const inputId = `klaerfall-slot-${i}`;
            return (
              <div key={i} style={{
                // Wrap + shrinkable input: in a squished detail panel the input drops below the
                // label instead of overflowing and painting over it ([SLOT]-overlap fix).
                display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                columnGap: theme.spacing.sm, rowGap: theme.spacing.xs,
                padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
                borderTop: idx === 0 ? 'none' : `1px solid ${theme.colors.bgLight}`,
              }}>
                <NumChip n={rowNo} />
                <div style={{ flex: '1 1 150px', minWidth: 0 }}>
                  <label htmlFor={inputId} title={seg.label} style={{
                    display: 'block', fontSize: 12, fontWeight: 600, color: theme.colors.navyMid,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{seg.label}</label>
                  {!confirmed && (
                    <span style={{ fontSize: 11, color: isHybrid ? tint.text : theme.colors.navy }}>
                      {isSystem ? 'aus System nachschlagen' : 'aus Mail – bitte prüfen'}
                      {isHybrid && (
                        <>
                          {' · '}<span style={{ color: theme.colors.navyMuted }}>Enter = bestätigen</span>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8,
                            color: tint.text,
                          }}>
                            <span aria-hidden style={{
                              width: 7, height: 7, borderRadius: '50%', backgroundColor: tint.border,
                              display: 'inline-block',
                            }} />
                            unbestätigt
                          </span>
                        </>
                      )}
                    </span>
                  )}
                </div>
                <div style={{ position: 'relative', flex: '1 1 160px', minWidth: 140 }}>
                  <input
                    id={inputId}
                    ref={el => draft.registerInput(i, el)}
                    value={value}
                    // The served slot hint (part 5-2): where the value comes from, read inside the
                    // field it goes into. Absent ⇒ no placeholder and no hover, never an empty one.
                    placeholder={seg.hintDe ?? undefined}
                    title={seg.hintDe ?? undefined}
                    readOnly={confirmed}
                    onChange={e => draft.setVal(i, e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && hasValue) { e.preventDefault(); draft.confirm(i); }
                      if (e.key === 'Escape') { e.preventDefault(); draft.clear(i); }
                    }}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      fontFamily: theme.font.family, fontSize: 13,
                      padding: confirmed ? '6px 30px 6px 8px' : (hasValue ? '6px 52px 6px 8px' : '6px 8px'),
                      borderRadius: theme.borderRadius.sm,
                      border: `1px solid ${confirmed ? theme.colors.borderLight : tintSolid}`,
                      backgroundColor: confirmed ? theme.colors.bgLight : theme.colors.white,
                      color: confirmed ? '#0f5f57' : theme.colors.navyMid,
                      fontWeight: confirmed ? 600 : 400,
                    }}
                  />
                  <div style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 4 }}>
                    {confirmed ? (
                      <button type="button" aria-label={`${seg.label} bearbeiten`} title="Bearbeiten" onClick={() => draft.unlock(i)} style={iconBtn('transparent')}><PencilIcon size={13} color={theme.colors.navyMid} /></button>
                    ) : hasValue ? (
                      <>
                        <button type="button" aria-label={`${seg.label} bestätigen`} title="Bestätigen (Enter)" onClick={() => draft.confirm(i)} style={iconBtn(tintSolid)}><CheckIcon size={12} color="#fff" /></button>
                        <button type="button" aria-label={`${seg.label} leeren`} title="Leeren (Esc)" onClick={() => draft.clear(i)} style={iconBtn(theme.colors.bgLight)}><CrossIcon size={12} color={theme.colors.navyMuted} /></button>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default KlaerfallEditor;
